import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';

export interface ProductVariantRow {
  variant_id: string;
  product_id: string;
  product_name: string;
  sku: string;
  size: string | null;
  color: string | null;
  on_hand: number;
}

const STALE = 30 * 1000;

/**
 * Flat list of active variants for the current company, with on-hand summed
 * across locations. Used by the variants list page (#770) so owners can reach
 * any variant — not just ones currently triggering a low-stock alert.
 *
 * Sorted by product name then size to mirror how owners think about gis/belts.
 */
export function useProductsWithVariants() {
  const { company } = useCurrentCompany();

  return useQuery({
    queryKey: ['inventory', 'products-with-variants', company?.id],
    enabled: !!company?.id,
    staleTime: STALE,
    queryFn: async (): Promise<ProductVariantRow[]> => {
      const { data, error } = await supabase
        .from('product_variants')
        .select(`
          id,
          sku,
          size,
          color,
          is_active,
          products!inner ( id, name, company_id, is_active ),
          inventory_levels ( quantity )
        `)
        .eq('is_active', true)
        .eq('products.company_id', company!.id)
        .eq('products.is_active', true);

      if (error) throw error;

      const rows = (data ?? []).map((v: any): ProductVariantRow => ({
        variant_id: v.id,
        product_id: v.products?.id ?? '',
        product_name: v.products?.name ?? '—',
        sku: v.sku,
        size: v.size ?? null,
        color: v.color ?? null,
        on_hand: (v.inventory_levels ?? []).reduce(
          (sum: number, l: { quantity: number }) => sum + (l.quantity ?? 0),
          0,
        ),
      }));

      rows.sort((a, b) => {
        const byProduct = a.product_name.localeCompare(b.product_name);
        if (byProduct !== 0) return byProduct;
        return (a.size ?? '').localeCompare(b.size ?? '');
      });

      return rows;
    },
  });
}
